import type { TFunction } from "i18next";
import { censusBoundLabel } from "../../../../utils/censusResolution";
import type { NurseRole } from "../../../../utils/models";
import { WEEKDAYS } from "../../../Settings/DepartmentConfiguration/utils";
import { type LocationConfigSlimData, shiftTimesOf } from "./locationConfigData";
import type { CensusScopeProblem } from "./locationEmrMapping";
import {
	findRatioLadderProblem,
	formatLadderDays,
	type LadderCoverageGap,
	type LadderRules,
	type RatioLadderProblem,
	toLocationRoles,
} from "./ratioLadderCoverage";

/**
 * Why the location popup can't save yet. A shift config (its hours, who works it, how it is staffed) is
 * checked by `shiftConfigProblem`; the location as a whole adds its name and its census choice. Pure:
 * `problemMessage` turns a problem into the Save tooltip's text.
 */

export type ShiftConfigProblem =
	| { kind: "times" }
	| { kind: "job" }
	| { kind: "roles" }
	| {
			kind: "ladder";
			problem: RatioLadderProblem;
			/** The ladder is checked role by role, so a gap's text names its role. */
			nameRoles: boolean;
	  }
	| { kind: "rowDays" }
	| { kind: "requiredOverTarget" }
	| { kind: "days" };

type LocationLevelProblem = { kind: "name" } | { kind: "census"; scope: CensusScopeProblem };

export type LocationConfigProblem = LocationLevelProblem | ShiftConfigProblem;

type ShiftLadderProblem = Extract<ShiftConfigProblem, { kind: "ladder" }>;

export type ShiftConfigContext = {
	/** The department's timezone, which shift hours are read in. */
	timezone: string;
	/** Synergy locations are staffed by facility type, so they need no job or roles. */
	isSynergyMS: boolean;
	jobManagementEnabled: boolean;
	/** A ratio department staffs a location by its ladder alone, so its staffing target rows aren't checked. */
	canEditRatioLadder: boolean;
	departmentRoles: NurseRole[];
	enabledJobCount: number;
};

export type LadderRulesContext = Pick<ShiftConfigContext, "jobManagementEnabled" | "departmentRoles" | "enabledJobCount"> & {
	/** The location's roles as ladder rows name them (`toLocationRoles`). */
	locationRoles: readonly NurseRole[];
};

/** How a location's ladder is checked: keyed by its job, or role by role, and what each row must name. */
export const ladderRulesFor = (ctx: LadderRulesContext): LadderRules => ({
	dimension: ctx.jobManagementEnabled ? { kind: "job" } : { kind: "roles", roles: ctx.locationRoles },
	requireJobPerRow: ctx.jobManagementEnabled && ctx.enabledJobCount > 1,
	requireRolesPerRow: !ctx.jobManagementEnabled && ctx.departmentRoles.length > 1,
});

/** Why the ladder can't be saved yet (ratio departments only), or null once it covers 0–∞. */
export const ratioLadderProblemOf = (data: Pick<LocationConfigSlimData, "roles" | "ratioLadder">, ctx: ShiftConfigContext): ShiftLadderProblem | null => {
	if (!ctx.canEditRatioLadder) return null;
	const locationRoles = toLocationRoles(data.roles, ctx.departmentRoles);
	const problem = findRatioLadderProblem(data.ratioLadder, ladderRulesFor({ ...ctx, locationRoles }));
	return problem === null ? null : { kind: "ladder", problem, nameRoles: !ctx.jobManagementEnabled && locationRoles.length > 1 };
};

/** The shift's hours, then who works it: a job with job management on, roles otherwise. */
const shiftIdentityProblem = (data: LocationConfigSlimData, ctx: ShiftConfigContext): ShiftConfigProblem | null => {
	const times = shiftTimesOf(data, ctx.timezone);
	if (!times.start.isValid || !times.end.isValid) return { kind: "times" };
	if (ctx.isSynergyMS) return null;
	if (ctx.jobManagementEnabled) return data.jobId ? null : { kind: "job" };
	return data.roles.length === 0 ? { kind: "roles" } : null;
};

/**
 * How the shift is staffed: by the ladder in a ratio department (its target rows are then never
 * checked), by the target rows otherwise — row by row, then the location's days as a whole.
 */
const shiftStaffingProblem = (data: LocationConfigSlimData, ctx: ShiftConfigContext): ShiftConfigProblem | null => {
	if (ctx.canEditRatioLadder) return ratioLadderProblemOf(data, ctx);
	for (const row of data.dayAmountRows) {
		if (row.days.length === 0) return { kind: "rowDays" };
		if (row.amountRequired > row.amount) return { kind: "requiredOverTarget" };
	}
	if (data.days.length === 0) return { kind: "days" };
	return null;
};

/** The first reason this shift config can't be saved, or null. */
export const shiftConfigProblem = (data: LocationConfigSlimData, ctx: ShiftConfigContext): ShiftConfigProblem | null => {
	const identity = shiftIdentityProblem(data, ctx);
	if (identity !== null) return identity;
	return shiftStaffingProblem(data, ctx);
};

export type LocationConfigProblemContext = ShiftConfigContext & {
	/** The census choice's problem (`censusScopeProblem`), or null. */
	censusScope: CensusScopeProblem | null;
};

/**
 * The first reason the popup can't save, or null. The order is the one the Save tooltip has always
 * used: name, hours, job or roles, census choice, then staffing (the ladder, or the target rows).
 */
export const locationConfigProblem = (data: LocationConfigSlimData, ctx: LocationConfigProblemContext): LocationConfigProblem | null => {
	if (!data.name || data.name.trim() === "") return { kind: "name" };
	const identity = shiftIdentityProblem(data, ctx);
	if (identity !== null) return identity;
	if (ctx.censusScope !== null) return { kind: "census", scope: ctx.censusScope };
	return shiftStaffingProblem(data, ctx);
};

/** "11–15", "31–∞", or a single census "0" when a gap is one value wide. */
const gapRangeLabel = (gap: LadderCoverageGap): string =>
	gap.start === gap.end ? censusBoundLabel(gap.start) : `${censusBoundLabel(gap.start)}–${censusBoundLabel(gap.end)}`;

/** Why Save waits on the census choice, as the Save tooltip says it. */
const CENSUS_SCOPE_PROBLEM_KEY: Record<CensusScopeProblem, string> = {
	missing: "schedule.template.census.scope.required",
	rooms_loading: "schedule.template.census.scope.rooms.loading",
	rooms_failed: "schedule.template.census.scope.rooms.failed",
};

/**
 * The text for a ladder that can't be saved. `nameRoles` prefixes a gap with its role, for locations
 * whose ladder is checked role by role.
 */
const ratioLadderProblemMessage = (problem: RatioLadderProblem, t: TFunction, nameRoles: boolean): string => {
	if (problem.kind !== "gap") return t(`schedule.template.ratio.ladder.error.${problem.kind}`);
	const when =
		problem.gap.days.length === WEEKDAYS.length
			? t("schedule.template.ratio.ladder.gap.days.all")
			: t("schedule.template.ratio.ladder.gap.days.some", { days: formatLadderDays(problem.gap.days) });
	const params = { range: gapRangeLabel(problem.gap), when };
	return problem.gap.role !== null && nameRoles
		? t("schedule.template.ratio.ladder.error.gap.role", { ...params, role: problem.gap.role })
		: t("schedule.template.ratio.ladder.error.gap", params);
};

/** The Save tooltip's text for a problem. */
export const problemMessage = (problem: LocationConfigProblem, t: TFunction): string => {
	switch (problem.kind) {
		case "name":
			return t("schedule.template.config.error.name");
		case "times":
			return t("schedule.template.config.error.times");
		case "job":
			return t("schedule.template.config.error.job");
		case "roles":
			return t("schedule.template.config.error.roles");
		case "census":
			return t(CENSUS_SCOPE_PROBLEM_KEY[problem.scope]);
		case "ladder":
			return ratioLadderProblemMessage(problem.problem, t, problem.nameRoles);
		case "rowDays":
			return t("schedule.template.config.error.row.days");
		case "requiredOverTarget":
			return t("schedule.template.config.error.required.over.target");
		case "days":
			return t("schedule.template.config.error.days");
	}
};
